import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { SelectTriggerSkeleton } from '@/components/core';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { dateOnlyOf, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import {
  useCreateTimeSheetEntry,
  useCurrentEmployeeSnapshot,
  useGetMbmProjectsLookup,
  useUpdateTimeSheetEntry,
} from '../../hooks/use-my-timesheet';
import {
  timesheetTaskFormSchema,
  TimesheetTaskFormValues,
} from '../../schemas/timesheet-task-form-schema';
import { MbmProjectOption, TimeSheetRecord, TimeSheetTask } from '../../types/timesheet.types';
import { orgToday } from '../../../utils/task-date';
import { formatHours, loggedHours, useScheduledHours } from '../../hooks/use-under-time';

export interface TimesheetTaskFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog edits this entry instead of creating a new one. */
  timeSheet?: TimeSheetRecord | null;
}

const emptyRow = { MbmProjectId: '', Billable: 0, GeneralAdmin: 0, SalesAndMarketing: 0 };

/**
 * Recursively translates every `message` found in a react-hook-form
 * FieldErrors tree via t(), mirroring hr-department-manager's resolver
 * pattern -- extended to walk nested paths since Tasks is a field array,
 * including array-level errors that land under a `root` key.
 */
const translateFieldErrors = (t: (key: string) => string, node: Record<string, any>): void => {
  if (!node || typeof node !== 'object') return;
  if (typeof node.message === 'string') {
    node.message = t(node.message);
  }
  Object.entries(node).forEach(([key, value]) => {
    if (key === 'message' || key === 'type' || key === 'ref') return;
    translateFieldErrors(t, value as Record<string, any>);
  });
};

export const TimesheetTaskFormDialog = ({
  open,
  onOpenChange,
  timeSheet,
}: TimesheetTaskFormDialogProps) => {
  const { t } = useTranslation();
  const employee = useCurrentEmployeeSnapshot();
  const { data: mbmProjectsData, isLoading: isProjectsLoading } = useGetMbmProjectsLookup();
  const mbmProjects =
    (mbmProjectsData as { MbmProjects?: { items: MbmProjectOption[] } })?.MbmProjects?.items ?? [];

  const { mutateAsync: createEntry, isPending: isCreating } = useCreateTimeSheetEntry();
  const { mutateAsync: updateEntry, isPending: isUpdating } = useUpdateTimeSheetEntry();
  const isPending = isCreating || isUpdating;
  const isEditing = Boolean(timeSheet);

  const form = useForm<TimesheetTaskFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(timesheetTaskFormSchema)(data, context, options);
      if (result.errors) {
        translateFieldErrors(t, result.errors);
      }
      return result;
    },
    defaultValues: { TaskDate: '', Tasks: [emptyRow] },
  });

  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'Tasks' });

  // Component Map › Add / Edit Task: a live day total against that day's working hours. Under them the
  // entry can still be saved ("Save anyway"); over 24 hours the schema blocks it.
  const scheduledHours = useScheduledHours();
  const watchedDate = form.watch('TaskDate');
  const watchedTasks = form.watch('Tasks');
  const dayTotal = (watchedTasks ?? []).reduce(
    (sum, task) =>
      sum +
      (Number(task.Billable) || 0) +
      (Number(task.GeneralAdmin) || 0) +
      (Number(task.SalesAndMarketing) || 0),
    0
  );
  const target = scheduledHours(watchedDate);
  const isUnder = target !== null && dayTotal < target;

  const tasksError = form.formState.errors.Tasks as
    { message?: string; root?: { message?: string } } | undefined;
  const tasksErrorMessage = tasksError?.message ?? tasksError?.root?.message;

  useEffect(() => {
    if (!open) return;
    if (timeSheet) {
      form.reset({
        TaskDate: dateOnlyOf(timeSheet.TaskDate),
        Tasks: timeSheet.Tasks.map((task) => ({
          MbmProjectId: task.MbmProjectId,
          Billable: task.Billable,
          GeneralAdmin: task.GeneralAdmin,
          SalesAndMarketing: task.SalesAndMarketing,
        })),
      });
    } else {
      form.reset({ TaskDate: orgToday(), Tasks: [emptyRow] });
    }
  }, [open, timeSheet, form]);

  const handleSubmit = async (values: TimesheetTaskFormValues) => {
    const tasks: TimeSheetTask[] = values.Tasks.map((task) => {
      const project = mbmProjects.find((option) => option.ItemId === task.MbmProjectId);
      return {
        MbmProjectId: task.MbmProjectId,
        ProjectId: project?.ProjectId,
        ProjectName: project?.ProjectName,
        CustomerId: project?.CustomerId,
        CustomerName: project?.CustomerName,
        Status: project?.Status,
        Billable: task.Billable,
        GeneralAdmin: task.GeneralAdmin,
        SalesAndMarketing: task.SalesAndMarketing,
        // Derived, not independently entered -- mirrors the Angular source's
        // AddTimesheetTaskDialogComponent._addTimesheetTaskList.
        NonBillable: task.GeneralAdmin + task.SalesAndMarketing,
      };
    });

    const customerIds = Array.from(
      new Set(tasks.map((task) => task.CustomerId).filter(Boolean))
    ) as string[];
    const projectIds = Array.from(
      new Set(tasks.map((task) => task.ProjectId).filter(Boolean))
    ) as string[];
    // The picked day as a date-only value (that day at 00:00:00 UTC); the form requires it.
    const taskDate = toDateOnlyValue(values.TaskDate);
    if (!taskDate) return;
    const dayTarget = scheduledHours(values.TaskDate);
    const isUnderTime = dayTarget !== null && loggedHours(tasks) < dayTarget;

    try {
      if (isEditing && timeSheet) {
        await updateEntry({
          itemId: timeSheet.ItemId,
          input: {
            Tasks: tasks,
            CustomerIds: customerIds,
            ProjectIds: projectIds,
            IsUnderTime: isUnderTime,
          },
        });
      } else {
        await createEntry({
          ...employee,
          TaskDate: taskDate,
          Tasks: tasks,
          CustomerIds: customerIds,
          ProjectIds: projectIds,
          // Against that day's working hours in the employee's HR term (see `useScheduledHours`).
          IsUnderTime: isUnderTime,
        });
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('MY_TIMESHEET_EDIT_TASK') : t('MY_TIMESHEET_ADD_TASK')}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="TaskDate"
              render={({ field }) => (
                <FormItem className="max-w-xs">
                  <FormLabel>{t('DATE')}</FormLabel>
                  <FormControl>
                    <DatePicker
                      disabled={isEditing}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-4">
              {fields.map((field, index) => (
                <div key={field.id} className="rounded-lg border border-border p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">
                      {t('MY_TIMESHEET_TASK')} {index + 1}
                    </span>
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(index)}
                        aria-label={t('MY_TIMESHEET_REMOVE_TASK')}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name={`Tasks.${index}.MbmProjectId`}
                    render={({ field: projectField }) => (
                      <FormItem>
                        <FormLabel>{t('MY_TIMESHEET_PROJECT')}</FormLabel>
                        {/* A saved task's project can't show its name until the projects load. */}
                        {isProjectsLoading ? (
                          <SelectTriggerSkeleton />
                        ) : (
                          <Select value={projectField.value} onValueChange={projectField.onChange}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t('MY_TIMESHEET_SELECT_PROJECT')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {mbmProjects.map((project) => (
                                <SelectItem key={project.ItemId} value={project.ItemId}>
                                  {project.CustomerName} / {project.ProjectName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3 [&>*]:min-w-0">
                    <FormField
                      control={form.control}
                      name={`Tasks.${index}.Billable`}
                      render={({ field: hoursField }) => (
                        <FormItem>
                          <FormLabel>{t('MY_TIMESHEET_BILLABLE_HOURS')}</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={0}
                              max={24}
                              step={0.25}
                              value={hoursField.value}
                              onChange={(e) => hoursField.onChange(e.target.valueAsNumber || 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`Tasks.${index}.GeneralAdmin`}
                      render={({ field: hoursField }) => (
                        <FormItem>
                          <FormLabel>{t('MY_TIMESHEET_GENERAL_ADMIN_HOURS')}</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={0}
                              max={24}
                              step={0.25}
                              value={hoursField.value}
                              onChange={(e) => hoursField.onChange(e.target.valueAsNumber || 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`Tasks.${index}.SalesAndMarketing`}
                      render={({ field: hoursField }) => (
                        <FormItem>
                          <FormLabel>{t('MY_TIMESHEET_SALES_MARKETING_HOURS')}</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={0}
                              max={24}
                              step={0.25}
                              value={hoursField.value}
                              onChange={(e) => hoursField.onChange(e.target.valueAsNumber || 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>

            <p
              role="status"
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm tabular-nums',
                isUnder
                  ? 'bg-warning-subtle text-warning-subtle-foreground'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              {isUnder && <TriangleAlert className="size-4 shrink-0" />}
              {isUnder
                ? t('MY_TIMESHEET_DAY_TOTAL_UNDER', {
                    total: formatHours(dayTotal),
                    target: formatHours(target ?? 0),
                  })
                : t('MY_TIMESHEET_DAY_TOTAL', { total: formatHours(dayTotal) })}
            </p>

            {tasksErrorMessage && (
              <p className="text-[0.8rem] font-medium text-destructive">{tasksErrorMessage}</p>
            )}

            {fields.length < 5 && (
              <Button
                type="button"
                variant="outline"
                className="w-full whitespace-normal sm:w-auto"
                onClick={() => append(emptyRow)}
              >
                <Plus className="size-4" />
                {t('MY_TIMESHEET_ADD_ANOTHER_TASK')}
              </Button>
            )}

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isPending} disabled={isPending}>
                {isUnder ? t('MY_TIMESHEET_SAVE_ANYWAY') : t('SAVE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
